import { useEffect, useState, useCallback } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, X, Loader2, Check, Wand2, Star, FileDown, Send, ShieldCheck,
  Plus, Trash2, TrendingUp, Lightbulb, Lock, ClipboardCheck, Compass, History, FileText,
  Award, Clock, RefreshCw, Target, CalendarDays, Download,
} from "lucide-react";
import PlanCoach from "@/components/PlanCoach";
import { CheckoutPromo } from "@/components/CheckoutPromo";
import { Calendar } from "@/components/ui/calendar";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Line, ComposedChart, Legend,
} from "recharts";

const STATUS_META = {
  draft: { label: "Draft", cls: "text-amber-300 bg-amber-500/15 border-amber-500/30" },
  under_review: { label: "Under Review", cls: "text-sky-300 bg-sky-500/15 border-sky-500/30" },
  approved: { label: "Approved", cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/30" },
};

const scoreColor = (n) => (n >= 70 ? "text-emerald-400" : n >= 40 ? "text-gold" : "text-amber-400");

export default function BusinessPlanBuilder({ mode = "client", projectId = null }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // section object
  const [busy, setBusy] = useState("");
  const [coach, setCoach] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showCover, setShowCover] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showGrants, setShowGrants] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);

  const base = mode === "admin"
    ? `/admin/business-plan-builder/${projectId}`
    : `/portal/business-plan`;
  const getUrl = mode === "admin"
    ? `/admin/business-plan-builder?project_id=${projectId}`
    : `/portal/business-plan`;

  const load = useCallback(() => {
    setLoading(true);
    api.get(getUrl).then((r) => setPlan(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load plan"))
      .finally(() => setLoading(false));
  }, [getUrl]);

  useEffect(() => { load(); }, [load]);

  // "Build my Business Plan" from the coach: instantly draft the Executive Summary so the
  // client sees real output and completeness moves off 0%. Kept to ONE section (fast, low
  // cost, no long synchronous multi-call generation) — the rest are drafted per-section.
  const buildPlan = async () => {
    setCoach(false);
    const t = toast.loading("Writing your first draft — the Executive Summary…");
    try {
      const { data } = await api.post(`${base}/section/executive_summary/draft`);
      if (data?.draft) await api.put(`${base}/section/executive_summary`, { content: data.draft });
      toast.success("Your Executive Summary draft is ready — review and edit it, then draft the rest.", { id: t });
    } catch {
      toast.error("Couldn't write your first draft — open Executive Summary below and press Draft with AI.", { id: t });
    } finally { load(); }
  };

  const setLevel = async (level) => {
    setBusy("level");
    try {
      await api.put(`${base}/financials`, { inputs: plan?.financial_inputs || {}, plan_level: level });
      load();
    } catch (e) { toast.error("Could not switch level"); } finally { setBusy(""); }
  };

  const submit = async () => {
    setBusy("submit");
    try { await api.post(`${base}/submit`); toast.success("Sent to ATF for review."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); } finally { setBusy(""); }
  };

  const setStatus = async (status) => {
    setBusy("status");
    try { await api.post(`${base}/status`, { status }); load(); }
    catch (e) { toast.error("Could not update status"); } finally { setBusy(""); }
  };

  const approve = async () => {
    setBusy("approve");
    try { const { data } = await api.post(`${base}/approve`); toast.success(`Approved & locked as v${data.version}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); } finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    if (mode === "client" && !plan.pdf_unlocked) { return setShowUnlock(true); }
    setBusy("pdf");
    try {
      const res = await api.get(`${base}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(plan?.org_name || "business")}-business-plan.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export PDF"); } finally { setBusy(""); }
  };

  const unlockPlan = async (discountCode = null) => {
    setBusy("unlock");
    try {
      const { data } = await api.post(`/checkout/session`, {
        origin_url: window.location.origin, kind: "plan_unlock", project_id: plan.project_id,
        discount_code: discountCode || undefined,
      });
      if (data?.url) window.location.href = data.url;
      else toast.error("Could not start checkout");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(""); }
  };

  const previewPlan = async () => {
    setBusy("preview");
    try {
      const res = await api.get(`/portal/business-plan/preview`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Could not open preview"); } finally { setBusy(""); }
  };

  const openOnePager = async () => {
    setBusy("onepager");
    try {
      const res = await api.get(`${base}/one-pager`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Could not open one-pager"); } finally { setBusy(""); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gold" size={28} /></div>;

  if (mode === "client" && coach) {
    return <PlanCoach onExit={() => { setCoach(false); load(); }} onFinish={buildPlan} />;
  }

  if (!plan || plan.no_project) {
    if (mode === "client") {
      return (
        <div className="bg-navy-800 border border-gold/25 p-10 text-center" data-testid="bp-start-coach">
          <Compass className="mx-auto text-gold mb-3" size={32} />
          <div className="text-white font-heading text-xl mb-1">Let's build your business, one question at a time</div>
          <p className="text-sm text-gray-400 max-w-lg mx-auto mb-6">Our Plan Coach walks you through the essentials — no jargon, no blank pages. Your answers become your Business Profile and draft your full plan.</p>
          <button onClick={() => setCoach(true)} data-testid="bp-launch-coach-empty"
            className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 font-semibold hover:bg-gold-light transition-colors">
            <Compass size={17} /> Start with the Plan Coach
          </button>
        </div>
      );
    }
    return (
      <div className="bg-navy-800 border border-white/10 p-10 text-center" data-testid="bp-no-project">
        <ClipboardCheck className="mx-auto text-gold mb-3" size={30} />
        <div className="text-white font-heading text-lg mb-1">No plan started for this project yet</div>
        <p className="text-sm text-gray-400">The client hasn't started their Business Plan. It builds from their Business Profile.</p>
      </div>
    );
  }

  const sm = STATUS_META[plan.status] || STATUS_META.draft;
  const locked = mode === "client" && plan.status !== "draft";

  return (
    <div data-testid="business-plan-builder">
      {/* Header */}
      <div className="bg-navy-800 border border-white/10 p-6 mb-6" data-testid="bp-header">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="text-white font-heading text-xl">{plan.org_name || "Your Business"}</span>
              <span className={`text-[11px] px-2.5 py-0.5 border ${sm.cls}`} data-testid="bp-status-badge">{sm.label}</span>
              {plan.current_version > 0 && <span className="text-[11px] text-gray-400 flex items-center gap-1"><Lock size={11} /> Approved v{plan.current_version}</span>}
            </div>
            <div className="text-xs text-gray-500 mt-1">{plan.industry} · Business Profile {plan.dna_score}% complete · SBA 9-section format</div>
            <div className="text-xs text-emerald-400/90 mt-1.5 flex items-center gap-1.5" data-testid="bp-human-review-note"><ShieldCheck size={12} /> Drafted with AI, reviewed by our team — nothing is finalized or sent without a human check.</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {mode === "client" && (
              <button onClick={() => setCoach(true)} data-testid="bp-launch-coach"
                className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 transition-colors">
                <Compass size={13} /> Plan Coach
              </button>
            )}
            <div className="flex border border-white/15 overflow-hidden" data-testid="bp-level-toggle">
              {["starter", "investor"].map((lv) => (
                <button key={lv} onClick={() => setLevel(lv)} disabled={busy === "level" || locked} data-testid={`bp-level-${lv}`}
                  className={`px-3 py-1.5 text-xs capitalize transition-colors ${plan.plan_level === lv ? "bg-gold text-navy font-semibold" : "text-gray-400 hover:text-white"}`}>
                  {lv === "starter" ? "Starter" : "Growth / Investor"}
                </button>
              ))}
            </div>
            <button onClick={() => setShowVersions(true)} data-testid="bp-versions-btn"
              className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors">
              <History size={13} /> Versions{plan.current_version > 0 ? ` (v${plan.current_version})` : ""}
            </button>
            <button onClick={() => setShowCover(true)} data-testid="bp-cover-letter-btn"
              className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors">
              <FileText size={13} /> Cover Letter
            </button>
            <button onClick={() => setShowCompare(true)} data-testid="bp-compare-loans-btn"
              className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors">
              <TrendingUp size={13} /> Compare Loans
            </button>
            <button onClick={() => setShowGrants(true)} data-testid="bp-grants-btn"
              className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors">
              <Compass size={13} /> Grant Finder
            </button>
            <button onClick={openOnePager} disabled={busy === "onepager"} data-testid="bp-one-pager-btn"
              className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors disabled:opacity-60">
              {busy === "onepager" ? <Loader2 className="animate-spin" size={13} /> : <FileText size={13} />} One-Pager
            </button>
            {mode === "client" && (
              <button onClick={() => setShowShare(true)} data-testid="bp-share-onepager-btn"
                className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors">
                <Send size={13} /> Share
              </button>
            )}
            {mode === "client" && !plan.pdf_unlocked && (
              <button onClick={previewPlan} disabled={busy === "preview"} data-testid="bp-preview-pdf"
                className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors disabled:opacity-60">
                {busy === "preview" ? <Loader2 className="animate-spin" size={13} /> : <FileText size={13} />} Preview
              </button>
            )}
            {mode === "client" && !plan.pdf_unlocked ? (
              <button onClick={downloadPdf} disabled={busy === "unlock"} data-testid="bp-unlock-pdf"
                className="inline-flex items-center gap-1.5 text-xs bg-gold text-navy font-semibold px-3 py-1.5 hover:bg-gold-light transition-colors disabled:opacity-60">
                {busy === "unlock" ? <Loader2 className="animate-spin" size={13} /> : <Lock size={13} />} Unlock Lender PDF{plan.unlock_price ? ` — $${plan.unlock_price}` : ""}
              </button>
            ) : (
              <button onClick={downloadPdf} disabled={busy === "pdf"} data-testid="bp-export-pdf"
                className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-white px-3 py-1.5 hover:border-gold/50 transition-colors disabled:opacity-60">
                {busy === "pdf" ? <Loader2 className="animate-spin" size={13} /> : <FileDown size={13} />} {mode === "client" ? "Download Plan PDF" : "Export PDF"}
              </button>
            )}
          </div>
        </div>
        {/* Progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-gray-400">Plan completeness</span>
            <span className={scoreColor(plan.progress)} data-testid="bp-progress">{plan.progress}%</span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div className="h-full bg-gold" style={{ width: `${plan.progress}%` }} />
          </div>
        </div>
        {/* Workflow actions */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {mode === "client" && plan.status === "draft" && (
            <button onClick={submit} disabled={busy === "submit"} data-testid="bp-submit-review"
              className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {busy === "submit" ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Submit for ATF Review
            </button>
          )}
          {mode === "client" && plan.status === "under_review" && (
            <span className="text-sm text-sky-300 flex items-center gap-2"><ShieldCheck size={15} /> ATF is reviewing your plan.</span>
          )}
          {mode === "admin" && (
            <>
              <span className="text-xs text-gray-500">Review actions:</span>
              <button onClick={() => setStatus("draft")} disabled={busy === "status"} data-testid="bp-set-draft"
                className="text-xs border border-white/15 text-gray-300 px-3 py-1.5 hover:border-amber-400/50">Return to Draft</button>
              <button onClick={() => setStatus("under_review")} disabled={busy === "status"} data-testid="bp-set-review"
                className="text-xs border border-white/15 text-gray-300 px-3 py-1.5 hover:border-sky-400/50">Mark Under Review</button>
              <button onClick={approve} disabled={busy === "approve"} data-testid="bp-approve"
                className="inline-flex items-center gap-2 bg-emerald-500 text-navy px-4 py-1.5 text-xs font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-60">
                {busy === "approve" ? <Loader2 className="animate-spin" size={13} /> : <Lock size={13} />} Approve &amp; Lock Version
              </button>
            </>
          )}
        </div>
      </div>

      {/* First-time coaching nudge (truly new) vs. next-step guidance (profile started) */}
      {mode === "client" && plan.dna_score < 20 && plan.progress === 0 ? (
        <div className="bg-gold/5 border border-gold/25 p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="bp-coach-nudge">
          <div className="flex items-start gap-3">
            <Compass className="text-gold shrink-0 mt-0.5" size={20} />
            <div>
              <div className="text-white font-medium">New to this? Let the Plan Coach guide you.</div>
              <p className="text-xs text-gray-400">Answer a few simple questions and we'll build your Business Profile and draft your plan for you.</p>
            </div>
          </div>
          <button onClick={() => setCoach(true)} data-testid="bp-coach-nudge-btn"
            className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors whitespace-nowrap">
            <Compass size={15} /> Start Plan Coach
          </button>
        </div>
      ) : mode === "client" && plan.status === "draft" && plan.progress < 100 ? (
        <div className="bg-gold/5 border border-gold/25 p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="bp-next-step">
          <div className="flex items-start gap-3">
            <Lightbulb className="text-gold shrink-0 mt-0.5" size={20} />
            <div>
              <div className="text-white font-medium">{plan.progress === 0 ? "Your profile is in — here's what's next." : "Keep going — finish your draft."}</div>
              <p className="text-xs text-gray-400">Open any section below and press <span className="text-gold font-medium">Draft with AI</span>. We'll write the first draft from your answers — you edit it, then <span className="text-gold font-medium">Submit for ATF Review</span>. Nothing is finalized or sent without our team's check.</p>
            </div>
          </div>
          <button onClick={() => setCoach(true)} data-testid="bp-next-step-coach"
            className="inline-flex items-center gap-2 text-xs bg-gold/15 text-gold border border-gold/30 px-4 py-2 hover:bg-gold/25 transition-colors whitespace-nowrap">
            <Compass size={14} /> Revisit Plan Coach
          </button>
        </div>
      ) : null}

      {/* Sections grid */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="bp-sections">
        {plan.sections.map((s, i) => (
          <button key={s.key} onClick={() => setActive(s)} data-testid={`bp-section-${s.key}`}
            className="text-left bg-navy-800 border border-white/10 hover:border-gold/40 transition-colors p-5">
            <div className="flex items-center justify-between gap-3 mb-1">
              <span className="text-white font-medium flex items-center gap-2">
                <span className="text-gold/60 text-xs font-mono">{String(i + 1).padStart(2, "0")}</span>{s.label}
              </span>
              {s.complete
                ? <span className="text-[10px] text-emerald-300 bg-emerald-500/15 px-2 py-0.5 inline-flex items-center gap-1"><Check size={10} /> Done</span>
                : <span className="text-[10px] text-gray-500 bg-white/5 px-2 py-0.5">Pending</span>}
            </div>
            <p className="text-xs text-gray-500 mb-3">{s.description}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {s.has_draft && <span className="text-[10px] text-gold bg-gold/15 px-2 py-0.5 inline-flex items-center gap-1"><Sparkles size={10} /> AI draft</span>}
              {s.what_we_know.length > 0
                ? s.what_we_know.map((k) => <span key={k.label} className="text-[10px] text-gray-400 bg-white/5 px-2 py-0.5">{k.label}</span>)
                : s.key !== "financial_projections" && <span className="text-[10px] text-gray-600">No profile data yet</span>}
              {s.key === "financial_projections" && <span className="text-[10px] text-gold/80">P&amp;L · Break-even · Cash</span>}
            </div>
          </button>
        ))}
      </div>

      {active && active.key === "financial_projections" && (
        <FinancialsModal base={base} getUrl={getUrl} plan={plan} locked={locked} onClose={() => setActive(null)} onSaved={load} />
      )}
      {active && active.key !== "financial_projections" && (
        <SectionModal base={base} section={active} locked={locked} onClose={() => setActive(null)} onSaved={load} />
      )}
      {showVersions && (
        <VersionsModal versionsUrl={mode === "admin" ? `/admin/business-plan-builder/${projectId}/versions` : "/portal/business-plan/versions"}
          onClose={() => setShowVersions(false)} />
      )}
      {showCover && (
        <CoverLetterModal base={base} plan={plan} locked={locked} onClose={() => setShowCover(false)} onSaved={load} />
      )}
      {showCompare && (
        <LoanComparisonModal base={base} plan={plan} locked={locked} onClose={() => setShowCompare(false)} onSaved={load} />
      )}
      {showGrants && (
        <GrantsModal base={base} plan={plan} onSaved={load} onClose={() => setShowGrants(false)} />
      )}
      {showShare && (
        <OnePagerShareModal plan={plan} onSent={load} onClose={() => setShowShare(false)} />
      )}
      {showUnlock && (
        <UnlockPlanModal plan={plan} busy={busy === "unlock"} onUnlock={unlockPlan} onClose={() => setShowUnlock(false)} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
function SectionModal({ base, section, locked, onClose, onSaved }) {
  const [content, setContent] = useState(section.content || section.ai_draft || "");
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const save = async () => {
    setSaving(true);
    try { await api.put(`${base}/section/${section.key}`, { content }); toast.success("Saved"); onSaved?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const generate = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`${base}/section/${section.key}/draft`);
      setContent(data.draft);
      toast.success("AI draft ready — review and edit, then save.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); } finally { setDrafting(false); }
  };

  return (
    <Modal title={section.label} desc={section.description} onClose={onClose} testid="bp-section-modal">
      {section.what_we_know.length > 0 && (
        <div className="bg-navy-800 border border-white/5 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gold mb-2">What we already know (from Business Profile)</div>
          <div className="space-y-1.5">
            {section.what_we_know.map((k) => (
              <div key={k.label} className="flex items-center justify-between text-sm text-gray-300">
                <span>{k.label}</span>
                <span className="flex gap-0.5">{[1, 2, 3].map((n) => <Star key={n} size={12} className={n <= k.stars ? "text-gold fill-gold" : "text-gray-600"} />)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-white font-medium">Section content</label>
          {!locked && (
            <button onClick={generate} disabled={drafting} data-testid="bp-generate-btn"
              className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 transition-colors disabled:opacity-60">
              {drafting ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />} {content ? "Regenerate with AI" : "Draft with AI"}
            </button>
          )}
        </div>
        <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={12} disabled={locked}
          placeholder="Click 'Draft with AI' to generate this section from your Business Profile — then review and edit."
          data-testid="bp-section-content"
          className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-y leading-relaxed disabled:opacity-70" />
      </div>
      {!locked && (
        <div className="flex justify-end gap-3 pt-2 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="bp-section-save"
            className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Save
          </button>
        </div>
      )}
    </Modal>
  );
}

// ----------------------------------------------------------------------------
const DEFAULT_FIN = {
  currency: "USD", startup_costs: [], avg_sale_price: "", sales_per_month: "",
  revenue_growth_pct: "", cogs_pct: "", operating_expenses: [], employee_count: "",
  avg_monthly_wage: "", starting_cash: "", annual_growth_pct: 15,
  loan_amount: "", loan_interest_pct: "", loan_term_months: "", use_of_funds: [], raise_type: "loan",
};

function FinancialsModal({ base, getUrl, plan, locked, onClose, onSaved }) {
  const [inp, setInp] = useState({ ...DEFAULT_FIN, ...(plan.financial_inputs || {}) });
  const [fin, setFin] = useState(plan.financials || null);
  const [narrative, setNarrative] = useState(
    plan.sections.find((s) => s.key === "financial_projections")?.content || "");
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [sugg, setSugg] = useState(null);
  const level = plan.plan_level;

  const set = (k, v) => setInp((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" || v == null ? "" : v);

  const addLine = (key) => set(key, [...(inp[key] || []), { label: "", amount: "" }]);
  const setLine = (key, i, field, val) => {
    const arr = [...(inp[key] || [])]; arr[i] = { ...arr[i], [field]: val }; set(key, arr);
  };
  const rmLine = (key, i) => set(key, (inp[key] || []).filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      // normalize numbers
      const clean = { ...inp };
      ["startup_costs", "operating_expenses", "use_of_funds"].forEach((k) => {
        clean[k] = (clean[k] || []).filter((x) => x.label || x.amount)
          .map((x) => ({ label: x.label, amount: Number(x.amount) || 0 }));
      });
      await api.put(`${base}/financials`, { inputs: clean, plan_level: level });
      await api.put(`${base}/section/financial_projections`, { content: narrative });
      const { data } = await api.get(getUrl);
      setFin(data.financials);
      toast.success("Financials updated — P&L recalculated.");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`${base}/financials/suggest`, { industry: plan.industry });
      setSugg(data);
      toast.success(data.ai_connected ? "Industry benchmarks ready (estimates only)." : "Showing general benchmark estimates.");
    } catch (e) { toast.error("Could not fetch suggestions"); } finally { setSuggesting(false); }
  };

  const generateNarrative = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`${base}/section/financial_projections/draft`);
      setNarrative(data.draft);
      toast.success("AI narrative drafted — review and edit.");
    } catch (e) { toast.error("Draft failed"); } finally { setDrafting(false); }
  };

  const mid = (a) => Math.round(((Number(a.low) + Number(a.high)) / 2) / 10) * 10;

  return (
    <Modal title="Financial Projections" desc="Enter simple inputs — we auto-build your P&L, break-even and cash outlook." onClose={onClose} testid="bp-financials-modal" wide>
      {/* AI benchmark helper */}
      {!locked && (
        <div className="bg-gold/5 border border-gold/20 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-gray-300 flex items-center gap-2"><Lightbulb size={15} className="text-gold" /> Not sure where to start? Get industry benchmark ranges.</div>
            <button onClick={suggest} disabled={suggesting} data-testid="bp-suggest-financials"
              className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 disabled:opacity-60">
              {suggesting ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Suggest with AI
            </button>
          </div>
          {sugg && (
            <div className="mt-3 text-xs text-gray-400 space-y-2" data-testid="bp-suggestions">
              <div className="text-amber-300/90 italic">{sugg.note}</div>
              <div className="flex flex-wrap gap-1.5">
                {(sugg.startup_costs || []).map((x) => (
                  <button key={x.label} onClick={() => set("startup_costs", [...(inp.startup_costs || []), { label: x.label, amount: mid(x) }])}
                    className="border border-white/15 px-2 py-1 hover:border-gold/50 text-gray-300">
                    + {x.label} <span className="text-gold">${x.low}–${x.high}</span>
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(sugg.operating_expenses || []).map((x) => (
                  <button key={x.label} onClick={() => set("operating_expenses", [...(inp.operating_expenses || []), { label: x.label, amount: mid(x) }])}
                    className="border border-white/15 px-2 py-1 hover:border-sky-400/50 text-gray-300">
                    + {x.label}/mo <span className="text-sky-300">${x.low}–${x.high}</span>
                  </button>
                ))}
              </div>
              {sugg.cogs_pct && <div>COGS typical: <span className="text-gold">{sugg.cogs_pct.typical}%</span> (range {sugg.cogs_pct.low}–{sugg.cogs_pct.high}%) — <button className="underline" onClick={() => set("cogs_pct", sugg.cogs_pct.typical)}>use</button></div>}
              {sugg.avg_sale_price && <div>Avg sale: <span className="text-gold">${sugg.avg_sale_price.low}–${sugg.avg_sale_price.high}</span> — <button className="underline" onClick={() => set("avg_sale_price", mid(sugg.avg_sale_price))}>use midpoint</button></div>}
            </div>
          )}
        </div>
      )}

      {!locked && (
        <>
          {/* Startup costs */}
          <LineListEditor label="One-time startup costs" testid="startup" lines={inp.startup_costs || []}
            onAdd={() => addLine("startup_costs")} onSet={(i, f, v) => setLine("startup_costs", i, f, v)} onRm={(i) => rmLine("startup_costs", i)} />

          {/* Revenue inputs */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Average sale price ($)" testid="avg-sale"><input type="number" value={num(inp.avg_sale_price)} onChange={(e) => set("avg_sale_price", e.target.value)} className={INPUT} /></Field>
            <Field label="Sales per month (start)" testid="sales-mo"><input type="number" value={num(inp.sales_per_month)} onChange={(e) => set("sales_per_month", e.target.value)} className={INPUT} /></Field>
            <Field label="Monthly revenue growth (%)" testid="rev-growth"><input type="number" value={num(inp.revenue_growth_pct)} onChange={(e) => set("revenue_growth_pct", e.target.value)} className={INPUT} /></Field>
            <Field label="Cost of goods (COGS %)" testid="cogs"><input type="number" value={num(inp.cogs_pct)} onChange={(e) => set("cogs_pct", e.target.value)} className={INPUT} /></Field>
          </div>

          {/* Operating expenses */}
          <LineListEditor label="Monthly operating expenses" testid="opex" lines={inp.operating_expenses || []}
            onAdd={() => addLine("operating_expenses")} onSet={(i, f, v) => setLine("operating_expenses", i, f, v)} onRm={(i) => rmLine("operating_expenses", i)} />

          {/* Team + cash */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Employees" testid="employees"><input type="number" value={num(inp.employee_count)} onChange={(e) => set("employee_count", e.target.value)} className={INPUT} /></Field>
            <Field label="Avg wage / employee / mo ($)" testid="wage"><input type="number" value={num(inp.avg_monthly_wage)} onChange={(e) => set("avg_monthly_wage", e.target.value)} className={INPUT} /></Field>
            <Field label="Starting cash / owner equity ($)" testid="starting-cash"><input type="number" value={num(inp.starting_cash)} onChange={(e) => set("starting_cash", e.target.value)} className={INPUT} /></Field>
          </div>

          {/* Financing (optional) — powers the lender/investor Sources & Uses + debt-service snapshot */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wider text-gold">Financing (optional)</div>
              <select value={inp.raise_type || "loan"} onChange={(e) => set("raise_type", e.target.value)} data-testid="raise-type"
                className="bg-navy border border-white/15 text-white text-xs px-2 py-1 focus:border-gold focus:outline-none">
                <option value="loan">Raising via Loan (debt)</option>
                <option value="equity">Raising via Equity / Grant</option>
              </select>
            </div>
            <a href="/funding-readiness" target="_blank" rel="noopener noreferrer" data-testid="bp-funding-readiness-link"
              className="flex items-center gap-2 mb-3 border border-gold/30 bg-gold/5 px-3 py-2 text-xs text-gray-300 hover:border-gold/60 transition-colors">
              <ShieldCheck size={14} className="text-gold shrink-0" />
              <span>New to borrowing? <span className="text-gold font-semibold">Check your funding readiness first</span> — see how lender-ready you appear and what to fix before you ask.</span>
            </a>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label={inp.raise_type === "equity" ? "Amount raised ($)" : "Loan amount requested ($)"} testid="loan-amount"><input type="number" value={num(inp.loan_amount)} onChange={(e) => set("loan_amount", e.target.value)} className={INPUT} /></Field>
              {inp.raise_type !== "equity" && <>
                <Field label="Interest rate (annual %)" testid="loan-rate"><input type="number" value={num(inp.loan_interest_pct)} onChange={(e) => set("loan_interest_pct", e.target.value)} className={INPUT} /></Field>
                <Field label="Term (months)" testid="loan-term"><input type="number" value={num(inp.loan_term_months)} onChange={(e) => set("loan_term_months", e.target.value)} className={INPUT} /></Field>
              </>}
            </div>
            <div className="mt-4">
              <LineListEditor label="How the funds will be used (Sources & Uses — optional)" testid="use-of-funds" lines={inp.use_of_funds || []}
                onAdd={() => addLine("use_of_funds")} onSet={(i, f, v) => setLine("use_of_funds", i, f, v)} onRm={(i) => rmLine("use_of_funds", i)} />
              <p className="text-[11px] text-gray-400 mt-1 italic">Itemize exactly how the loan/equity will be spent (e.g. Equipment $20k, Vehicle $15k, Marketing $5k). Leave blank to auto-derive from startup costs.</p>
            </div>
          </div>
          {level === "investor" && (
            <Field label="Annual growth for 3-yr projection (%)" testid="annual-growth"><input type="number" value={num(inp.annual_growth_pct)} onChange={(e) => set("annual_growth_pct", e.target.value)} className={INPUT} /></Field>
          )}

          <div className="flex justify-end">
            <button onClick={save} disabled={saving} data-testid="bp-financials-save"
              className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Save &amp; Recalculate
            </button>
          </div>
        </>
      )}

      {/* Computed results */}
      {fin && <FinancialResults fin={fin} />}

      {/* Narrative */}
      <div className="pt-4 border-t border-white/10">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-white font-medium">Financial narrative (explains the numbers)</label>
          {!locked && (
            <button onClick={generateNarrative} disabled={drafting} data-testid="bp-fin-narrative-draft"
              className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 disabled:opacity-60">
              {drafting ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />} Draft with AI
            </button>
          )}
        </div>
        <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={5} disabled={locked}
          data-testid="bp-fin-narrative"
          className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-y leading-relaxed disabled:opacity-70" />
        <p className="text-[11px] text-amber-300/80 mt-2 italic">Financial figures are your own estimates for planning purposes — not guarantees of performance.</p>
      </div>
    </Modal>
  );
}

function FinancialResults({ fin }) {
  const chartData = (fin.months || []).map((m) => ({
    name: `M${m.month}`, Revenue: m.revenue, Net: m.net, Cash: m.cash,
  }));
  const be = fin.break_even || {};
  return (
    <div className="space-y-5" data-testid="bp-financial-results">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Startup cost" value={money(fin.startup_total)} />
        <Stat label="Year 1 revenue" value={money(fin.year1?.revenue)} />
        <Stat label="Year 1 net" value={money(fin.year1?.net)} tone={fin.year1?.net >= 0 ? "pos" : "neg"} />
        <Stat label="Gross margin" value={`${fin.gross_margin_pct}%`} />
        <Stat label="Break-even / mo" value={be.monthly_revenue != null ? money(be.monthly_revenue) : "—"} />
        <Stat label="Break-even units" value={be.monthly_units != null ? be.monthly_units : "—"} />
        <Stat label="Profitable from" value={be.profitable_from_month ? `Month ${be.profitable_from_month}` : "Not in Yr 1"} tone={be.profitable_from_month ? "pos" : "neg"} />
        <Stat label="Lowest cash" value={money(fin.cash?.lowest_point)} tone={fin.cash?.runway_warning ? "neg" : "pos"} />
      </div>

      <div className="bg-navy-800 border border-white/10 p-4">
        <div className="text-[11px] uppercase tracking-wider text-gold mb-3">12-Month Revenue vs Net Profit</div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData}>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#9aa4b2", fontSize: 11 }} />
              <YAxis tick={{ fill: "#9aa4b2", fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0B1220", border: "1px solid #ffffff20", color: "#fff" }} formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Revenue" fill="#C8A45C" radius={[2, 2, 0, 0]} />
              <Line type="monotone" dataKey="Net" stroke="#34d399" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Cash" stroke="#38bdf8" strokeWidth={2} strokeDasharray="4 3" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {fin.runway_warning || fin.cash?.runway_warning ? (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 p-3">
          ⚠ Cash goes negative during year one (lowest point {money(fin.cash?.lowest_point)}). Consider more starting capital or lower early expenses.
        </div>
      ) : null}

      {fin.three_year && (
        <div className="bg-navy-800 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gold mb-3">3-Year Projection</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bp-three-year">
              <thead><tr className="text-gray-500 text-xs">
                <th className="text-left py-1.5">Year</th><th className="text-right">Revenue</th><th className="text-right">Gross</th><th className="text-right">Expenses</th><th className="text-right">Net</th>
              </tr></thead>
              <tbody>
                {fin.three_year.map((y) => (
                  <tr key={y.year} className="border-t border-white/5 text-gray-200">
                    <td className="py-2">Year {y.year}</td>
                    <td className="text-right">{money(y.revenue)}</td>
                    <td className="text-right">{money(y.gross)}</td>
                    <td className="text-right">{money(y.opex)}</td>
                    <td className={`text-right ${y.net >= 0 ? "text-emerald-400" : "text-red-400"}`}>{money(y.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {fin.funding && <FundingBlock funding={fin.funding} />}
    </div>
  );
}

function FundingBlock({ funding }) {
  const loan = funding.loan;
  return (
    <div className="space-y-4" data-testid="bp-funding-block">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-navy-800 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gold mb-3">Sources of Funds</div>
          <table className="w-full text-sm">
            <tbody>
              {(funding.sources || []).map((s, i) => (
                <tr key={i} className="border-t border-white/5 text-gray-200">
                  <td className="py-1.5">{s.label}</td><td className="text-right">{money(s.amount)}</td>
                </tr>
              ))}
              <tr className="border-t border-gold/30 text-white font-semibold">
                <td className="py-1.5">Total Sources</td><td className="text-right">{money(funding.sources_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="bg-navy-800 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gold mb-3">Use of Funds</div>
          <table className="w-full text-sm">
            <tbody>
              {(funding.uses || []).map((u, i) => (
                <tr key={i} className="border-t border-white/5 text-gray-200">
                  <td className="py-1.5">{u.label}</td><td className="text-right">{money(u.amount)}</td>
                </tr>
              ))}
              <tr className="border-t border-gold/30 text-white font-semibold">
                <td className="py-1.5">Total Uses</td><td className="text-right">{money(funding.uses_total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {loan && (
        <div className="bg-navy-800 border border-white/10 p-4" data-testid="bp-loan-snapshot">
          <div className="text-[11px] uppercase tracking-wider text-gold mb-3">Loan Repayment &amp; Debt-Service Snapshot</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Monthly payment" value={money(loan.monthly_payment)} />
            <Stat label="Annual debt service" value={money(loan.annual_debt_service)} />
            <Stat label="Total interest" value={money(loan.total_interest)} />
            <Stat label="Total repaid" value={money(loan.total_repaid)} />
            <Stat label="Rate (annual)" value={`${loan.annual_interest_pct}%`} />
            <Stat label="Term" value={`${loan.term_months} mo`} />
            {loan.dscr != null && (
              <Stat label="DSCR (Yr 1)" value={`${loan.dscr}x`} tone={loan.dscr >= 1.25 ? "pos" : "neg"} />
            )}
          </div>
          {loan.dscr != null && (
            <p className="text-[11px] text-gray-400 mt-3 italic">Lenders typically look for a Debt-Service Coverage Ratio of 1.25x or higher.</p>
          )}
        </div>
      )}
    </div>
  );
}

function CoverLetterModal({ base, plan, locked, onClose, onSaved }) {
  const cl = plan.cover_letter || {};
  const loan = plan.financials?.funding?.loan;
  const isEquity = plan.financials?.funding?.raise_type === "equity";
  const [inp, setInp] = useState({
    lender_name: cl.lender_name || "",
    loan_officer: cl.loan_officer || "",
    loan_amount: cl.loan_amount ?? (loan?.amount || ""),
    purpose: cl.purpose || "",
    content: cl.content || "",
  });
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const set = (k, v) => setInp((p) => ({ ...p, [k]: v }));

  const draft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`${base}/cover-letter/draft`, { ...inp, enabled: true });
      set("content", data.draft);
      toast.success("Cover letter drafted — review and edit.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Draft failed"); } finally { setDrafting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${base}/cover-letter`, { ...inp, enabled: true });
      toast.success("Cover letter saved — it's now the first page of your plan PDF.");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <Modal title={isEquity ? "Investor / Grant Cover Letter" : "Lender Cover Letter"} desc={isEquity ? "An optional investor/grant pitch letter — it becomes the first page of your plan PDF." : "An optional loan-request letter addressed to your bank — it becomes the first page of your plan PDF."} onClose={onClose} testid="bp-cover-letter-modal" wide>
      {!locked && (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={isEquity ? "Investor / Grant / Fund name" : "Lender / Bank name"} testid="cl-lender"><input value={inp.lender_name} onChange={(e) => set("lender_name", e.target.value)} className={INPUT} placeholder={isEquity ? "e.g. Growth Ventures Fund" : "e.g. First National Bank"} /></Field>
            <Field label={isEquity ? "Contact name (optional)" : "Loan officer (optional)"} testid="cl-officer"><input value={inp.loan_officer} onChange={(e) => set("loan_officer", e.target.value)} className={INPUT} placeholder="e.g. Jane Smith" /></Field>
            <Field label={isEquity ? "Amount sought ($)" : "Loan amount requested ($)"} testid="cl-amount"><input type="number" value={inp.loan_amount} onChange={(e) => set("loan_amount", e.target.value)} className={INPUT} /></Field>
            <Field label={isEquity ? "Use of funds" : "Purpose of loan"} testid="cl-purpose"><input value={inp.purpose} onChange={(e) => set("purpose", e.target.value)} className={INPUT} placeholder="e.g. Equipment & working capital" /></Field>
          </div>
          <div className="flex items-center justify-between mt-4 mb-2">
            <label className="text-sm text-white font-medium">Letter body</label>
            <button onClick={draft} disabled={drafting} data-testid="bp-cl-draft"
              className="inline-flex items-center gap-1.5 text-xs bg-gold/15 text-gold border border-gold/30 px-3 py-1.5 hover:bg-gold/25 disabled:opacity-60">
              {drafting ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />} Draft with AI
            </button>
          </div>
          <textarea value={inp.content} onChange={(e) => set("content", e.target.value)} rows={12} data-testid="bp-cl-body"
            className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-y leading-relaxed"
            placeholder="Click 'Draft with AI' to generate a first draft from your Business Profile and funding request, then edit freely." />
          <div className="flex justify-end mt-3">
            <button onClick={save} disabled={saving} data-testid="bp-cl-save"
              className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Save Cover Letter
            </button>
          </div>
        </>
      )}
      {locked && (
        <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{inp.content || "No cover letter yet."}</div>
      )}
    </Modal>
  );
}

function amortize(amount, annualPct, term) {
  const P = Number(amount) || 0, n = parseInt(term) || 0, r = (Number(annualPct) || 0) / 100 / 12;
  if (P <= 0 || n <= 0) return { monthly: 0, totalRepaid: 0, totalInterest: 0 };
  const monthly = r > 0 ? (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : P / n;
  const totalRepaid = monthly * n;
  return { monthly, totalRepaid, totalInterest: totalRepaid - P };
}

function LoanComparisonModal({ base, plan, locked, onClose, onSaved }) {
  const loan = plan.financials?.funding?.loan || {};
  const year1Net = plan.financials?.year1?.net || 0;
  const [amount, setAmount] = useState(loan.amount || plan.financials?.funding?.loan_amount || "");
  const [a, setA] = useState({ rate: loan.annual_interest_pct || 8.5, term: loan.term_months || 60 });
  const [b, setB] = useState({ rate: (loan.annual_interest_pct || 8.5) + 1, term: (loan.term_months || 60) + 24 });
  const [applying, setApplying] = useState("");
  const money = (x) => `$${(Number(x) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  const calc = (s) => {
    const r = amortize(amount, s.rate, s.term);
    const annualDebt = r.monthly * 12;
    const dscr = annualDebt > 0 ? year1Net / annualDebt : null;
    return { ...r, annualDebt, dscr };
  };
  const ra = calc(a), rb = calc(b);
  const betterInterest = ra.totalInterest <= rb.totalInterest ? "A" : "B";
  const betterDscr = (ra.dscr || 0) >= (rb.dscr || 0) ? "A" : "B";

  const saveScenarios = async () => {
    if (locked || !base) return;
    setApplying("both");
    try {
      const mk = (label, s, res) => ({ label, amount: Number(amount) || 0, rate: Number(s.rate) || 0,
        term: parseInt(s.term) || 0, monthly: res.monthly, total_interest: res.totalInterest,
        total_repaid: res.totalRepaid, dscr: res.dscr });
      await api.put(`${base}/loan-scenarios`, { scenarios: [mk("Option A", a, ra), mk("Option B", b, rb)] });
      toast.success("Both options saved to your plan — they'll appear in the PDF for your lender.");
      onSaved?.();
    } catch (e) { toast.error("Could not save scenarios"); } finally { setApplying(""); }
  };

  const applyScenario = async (key, s) => {
    if (locked || !base) return;
    setApplying(key);
    try {
      const inputs = { ...(plan.financial_inputs || {}), raise_type: "loan",
        loan_amount: Number(amount) || 0, loan_interest_pct: Number(s.rate) || 0, loan_term_months: parseInt(s.term) || 0 };
      await api.put(`${base}/financials`, { inputs });
      toast.success(`Scenario ${key.toUpperCase()} applied to your plan.`);
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply scenario"); } finally { setApplying(""); }
  };

  const Col = ({ label, s, setS, res, key1, tone }) => (
    <div className={`bg-navy-800 border p-4 ${tone ? "border-gold/50" : "border-white/10"}`} data-testid={`loan-scenario-${key1}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-white">{label}</span>
        {tone && <span className="text-[10px] uppercase tracking-wide bg-gold text-navy px-2 py-0.5 font-bold">Best</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <Field label="Rate (annual %)" testid={`cmp-rate-${key1}`}><input type="number" value={s.rate} onChange={(e) => setS({ ...s, rate: e.target.value })} className={INPUT} /></Field>
        <Field label="Term (months)" testid={`cmp-term-${key1}`}><input type="number" value={s.term} onChange={(e) => setS({ ...s, term: e.target.value })} className={INPUT} /></Field>
      </div>
      <dl className="space-y-1.5 text-sm">
        <Row k="Monthly payment" v={money(res.monthly)} strong />
        <Row k="Total interest" v={money(res.totalInterest)} />
        <Row k="Total repaid" v={money(res.totalRepaid)} />
        <Row k="Annual debt service" v={money(res.annualDebt)} />
        {res.dscr != null && <Row k="DSCR (Yr 1)" v={`${res.dscr.toFixed(2)}x`} tone={res.dscr >= 1.25 ? "pos" : "neg"} />}
      </dl>
      {!locked && (
        <button onClick={() => applyScenario(key1, s)} disabled={!!applying} data-testid={`use-scenario-${key1}`}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs bg-gold text-navy font-semibold px-3 py-2 hover:bg-gold-light transition-colors disabled:opacity-60">
          {applying === key1 ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Use this scenario
        </button>
      )}
    </div>
  );

  return (
    <Modal title="Compare Loan Scenarios" desc="Try different rates and terms side by side to pick the best financing before you print your plan." onClose={onClose} testid="loan-comparison-modal" wide>
      <Field label="Loan amount ($)" testid="cmp-amount"><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className={INPUT} /></Field>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Col label="Scenario A" s={a} setS={setA} res={ra} key1="a" tone={betterInterest === "A"} />
        <Col label="Scenario B" s={b} setS={setB} res={rb} key1="b" tone={betterInterest === "B"} />
      </div>
      <p className="text-xs text-gray-400 mt-4" data-testid="loan-compare-summary">
        Scenario <span className="text-gold font-semibold">{betterInterest}</span> costs less in total interest
        {ra.dscr != null && rb.dscr != null && <> · Scenario <span className="text-gold font-semibold">{betterDscr}</span> has the stronger debt-service coverage</>}.
        Lenders generally like a DSCR of 1.25x or higher.
      </p>
      {!locked && base && (
        <div className="flex justify-end mt-4">
          <button onClick={saveScenarios} disabled={applying === "both"} data-testid="save-both-scenarios"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 text-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
            {applying === "both" ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Save both options to my plan
          </button>
        </div>
      )}
    </Modal>
  );
}

function Row({ k, v, strong, tone }) {
  return (
    <div className="flex items-center justify-between border-t border-white/5 py-1">
      <dt className="text-gray-400">{k}</dt>
      <dd className={`${strong ? "text-white font-semibold" : "text-gray-200"} ${tone === "pos" ? "text-green-400" : tone === "neg" ? "text-red-400" : ""}`}>{v}</dd>
    </div>
  );
}

function OnePagerShareModal({ plan, onSent, onClose }) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState("");
  const sends = plan?.one_pager_sends || [];

  const send = async () => {
    if (!email.trim()) { toast.error("Enter the recipient's email"); return; }
    setSending(true);
    try {
      await api.post(`/portal/business-plan/one-pager/send`, { to_email: email.trim(), message: message.trim() });
      toast.success(`One-pager sent to ${email.trim()}.`);
      onSent?.();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); } finally { setSending(false); }
  };

  const resend = async (toEmail, msg) => {
    setResending(toEmail);
    try {
      await api.post(`/portal/business-plan/one-pager/send`, { to_email: toEmail, message: msg || "" });
      toast.success(`One-pager re-sent to ${toEmail}.`);
      onSent?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); } finally { setResending(""); }
  };

  return (
    <Modal title="Share Investor One-Pager" desc="Email the one-page executive teaser (as a PDF attachment) straight to an investor." onClose={onClose} testid="onepager-share-modal">
      <Field label="Investor email" testid="share-email"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} placeholder="investor@example.com" /></Field>
      <div className="mt-3">
        <label className="block text-sm text-white font-medium mb-1.5">Personal message (optional)</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="share-message"
          className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-y"
          placeholder="Add a short note to the investor…" />
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={send} disabled={sending} data-testid="share-send"
          className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Send one-pager
        </button>
      </div>
      {sends.length > 0 && (
        <div className="mt-5 border-t border-white/10 pt-4" data-testid="onepager-history">
          <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Previously sent to</div>
          <ul className="space-y-1.5">
            {sends.slice().reverse().map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-xs text-gray-300" data-testid={`onepager-history-row-${i}`}>
                <span className="min-w-0 truncate">{s.to}</span>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-gray-500">{s.at ? new Date(s.at).toLocaleDateString() : ""}</span>
                  <button onClick={() => resend(s.to, s.message)} disabled={resending === s.to} data-testid={`onepager-resend-${i}`}
                    className="inline-flex items-center gap-1 text-gold hover:text-gold-light disabled:opacity-50">
                    {resending === s.to ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />} Resend
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

function UnlockPlanModal({ plan, busy, onUnlock, onClose }) {
  const [promo, setPromo] = useState(null);
  const price = plan.unlock_price || 99;
  const finalPrice = promo ? Math.round(price * (1 - promo.percent / 100)) : price;
  return (
    <Modal title="Unlock your Lender-Ready PDF" desc="A polished, bank-ready PDF of your full business plan — yours to download and reuse forever." onClose={onClose} testid="bp-unlock-modal">
      <div className="bg-navy-800 border border-white/10 p-5 text-center">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">One-time unlock</div>
        <div className="font-heading text-4xl text-gold">
          ${finalPrice}
          {promo ? <span className="text-gray-500 text-2xl line-through ml-2">${price}</span> : null}
        </div>
        {promo && <div className="text-xs text-gold mt-1">{promo.code} — {promo.percent}% off applied</div>}
      </div>
      <div className="mt-4">
        <CheckoutPromo kind="plan_unlock" applied={promo} onApply={setPromo} onClear={() => setPromo(null)} />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-white/10 mt-4">
        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
        <button onClick={() => onUnlock(promo?.code)} disabled={busy} data-testid="bp-unlock-confirm"
          className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={15} /> : <Lock size={15} />} Continue to secure checkout
        </button>
      </div>
    </Modal>
  );
}

function GrantsModal({ base, plan, onSaved, onClose }) {
  const [loading, setLoading] = useState(true);
  const [grants, setGrants] = useState([]);
  const [err, setErr] = useState(false);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [pipeline, setPipeline] = useState(plan?.saved_grants || []);
  const [amtDraft, setAmtDraft] = useState({});
  const [goal, setGoal] = useState(plan?.funding_goal || 0);
  const [goalInput, setGoalInput] = useState(plan?.funding_goal ? String(plan.funding_goal) : "");
  const [savingGoal, setSavingGoal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const STATUSES = ["Saved", "Applied", "Awarded", "Declined"];

  useEffect(() => {
    let alive = true;
    api.post(`${base}/grants`).then((r) => { if (alive) { setGrants(r.data.grants || []); setLoading(false); } })
      .catch(() => { if (alive) { setErr(true); setLoading(false); } });
    return () => { alive = false; };
  }, [base]);

  const toggle = (i) => setSelected((s) => ({ ...s, [i]: !s[i] }));
  const chosen = grants.filter((_, i) => selected[i]);

  const persist = async (list) => {
    await api.put(`${base}/grants/save`, { grants: list });
    setPipeline(list);
    onSaved?.();
  };

  const saveGrants = async () => {
    setSaving(true);
    try {
      const merged = [...pipeline, ...chosen.filter((c) => !pipeline.some((p) => p.name === c.name))];
      await persist(merged);
      toast.success(`Added to your funding pipeline — they'll appear in your plan PDF.`);
      setSelected({});
    } catch (e) { toast.error("Could not save grants"); } finally { setSaving(false); }
  };

  const setStatus = async (idx, status) => {
    const list = pipeline.map((g, i) => (i === idx ? { ...g, status } : g));
    try {
      await persist(list);
      if (status === "Awarded") toast.success(`🎉 Congratulations! "${list[idx].name}" marked as Awarded.`);
      else toast.success(`Marked "${list[idx].name}" as ${status}.`);
    } catch (e) { toast.error("Could not update status"); }
  };
  const removeGrant = async (idx) => {
    try { await persist(pipeline.filter((_, i) => i !== idx)); } catch (e) { toast.error("Could not remove"); }
  };

  const amtValue = (i, g) => (amtDraft[i] !== undefined ? amtDraft[i] : (g.amount ? String(g.amount) : ""));
  const saveAmount = async (idx) => {
    if (amtDraft[idx] === undefined) return;
    const amount = Math.max(0, Number(amtDraft[idx]) || 0);
    const list = pipeline.map((g, i) => (i === idx ? { ...g, amount } : g));
    try { await persist(list); } catch (e) { toast.error("Could not save amount"); }
    finally { setAmtDraft((d) => { const n = { ...d }; delete n[idx]; return n; }); }
  };

  const closingSoon = (g) => {
    if (!g.deadline || g.status === "Awarded" || g.status === "Declined") return false;
    const d = new Date(g.deadline);
    if (isNaN(d.getTime())) return false;
    const days = (d.getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 30;
  };

  const awarded = pipeline.filter((g) => g.status === "Awarded");
  const totalSecured = awarded.reduce((s, g) => s + (Number(g.amount) || 0), 0);

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      const { data } = await api.put(`${base}/funding-goal`, { goal: Number(goalInput) || 0 });
      setGoal(data.funding_goal);
      toast.success("Funding target saved.");
    } catch (e) { toast.error("Could not save target"); } finally { setSavingGoal(false); }
  };

  const exportCsv = () => {
    const rows = [["Grant", "Provider", "Status", "Amount", "Window"],
      ...pipeline.map((g) => [g.name || "", g.provider || "", g.status || "Saved", g.amount || 0, g.deadline || ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "funding-pipeline.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`${base}/grants/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "funding-pipeline.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export PDF"); } finally { setExporting(false); }
  };

  const parseDate = (s) => { if (!s) return null; const d = new Date(s); return isNaN(d.getTime()) ? null : d; };
  const upcoming = pipeline
    .map((g) => ({ ...g, _d: parseDate(g.deadline) }))
    .filter((g) => g._d && g.status !== "Awarded" && g.status !== "Declined")
    .sort((a, b) => a._d - b._d);
  const deadlineDates = upcoming.map((g) => g._d);

  const statusColor = (s) => s === "Awarded" ? "text-green-400" : s === "Applied" ? "text-gold" : s === "Declined" ? "text-red-400" : "text-gray-400";

  return (
    <Modal title="Grant Finder & Pipeline" desc="Top Foster suggests small-business grants from your Business Profile. Save the ones you like, then track them as you apply. These are leads to verify — not guarantees." onClose={onClose} testid="grants-modal" wide>
      {pipeline.length > 0 && (
        <div className="mb-4 bg-navy-800 border border-white/10 p-4" data-testid="funding-progress">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-sm text-white font-semibold"><Target size={15} className="text-gold" /> Funding progress</div>
            <div className="flex items-center gap-1.5" data-testid="funding-goal-setter">
              <span className="text-xs text-gray-400">Target $</span>
              <input type="number" min="0" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} data-testid="funding-goal-input"
                className="w-28 bg-navy border border-white/15 text-white text-xs px-2 py-1 focus:border-gold focus:outline-none" placeholder="0" />
              <button onClick={saveGoal} disabled={savingGoal} data-testid="funding-goal-save"
                className="text-xs bg-gold/15 text-gold border border-gold/30 px-2.5 py-1 hover:bg-gold/25 disabled:opacity-60">{savingGoal ? "…" : "Set"}</button>
            </div>
          </div>
          {goal > 0 && (
            <>
              <div className="h-3 bg-navy border border-white/10 overflow-hidden">
                <div className="h-full bg-green-500 transition-all" data-testid="funding-bar"
                  style={{ width: `${Math.min(100, Math.round(totalSecured / goal * 100))}%` }} />
              </div>
              <div className="flex justify-between text-xs mt-1.5">
                <span className="text-green-400">${totalSecured.toLocaleString()} secured</span>
                <span className="text-gray-400">{Math.round(totalSecured / goal * 100)}% of ${goal.toLocaleString()}</span>
              </div>
              {totalSecured >= goal && (
                <div className="mt-2 text-sm text-green-300 font-semibold flex items-center gap-1.5" data-testid="funding-goal-reached">
                  <Award size={15} /> Goal reached — congratulations! 🎉
                </div>
              )}
            </>
          )}
          {awarded.length > 0 && (
            <div className="text-sm text-green-300 mt-2 flex items-center gap-1.5" data-testid="grants-secured-tally">
              <Award size={14} className="shrink-0" />
              {totalSecured > 0
                ? `Funded! Total secured: $${totalSecured.toLocaleString()} across ${awarded.length} grant${awarded.length > 1 ? "s" : ""}`
                : `Funded! ${awarded.length} grant${awarded.length > 1 ? "s" : ""} secured`}
            </div>
          )}
          {goal === 0 && awarded.length === 0 && (
            <div className="text-xs text-gray-500">Set a target raise above to track progress as grants are awarded.</div>
          )}
        </div>
      )}
      {pipeline.length > 0 && (
        <div className="mb-5" data-testid="grants-pipeline">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-gold">Your Funding Pipeline</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowCal((v) => !v)} data-testid="grants-calendar-toggle"
                className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gold"><CalendarDays size={13} /> Calendar</button>
              <button onClick={exportCsv} data-testid="grants-export-csv"
                className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gold"><Download size={13} /> CSV</button>
              <button onClick={exportPdf} disabled={exporting} data-testid="grants-export-pdf"
                className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-gold disabled:opacity-50">
                {exporting ? <Loader2 className="animate-spin" size={13} /> : <FileDown size={13} />} PDF</button>
            </div>
          </div>
          {showCal && (
            <div className="mb-3 bg-navy-800 border border-white/10 p-3" data-testid="grants-calendar">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="text-white">
                  <Calendar mode="single" defaultMonth={deadlineDates[0] || new Date()}
                    modifiers={{ deadline: deadlineDates }}
                    modifiersClassNames={{ deadline: "bg-gold/30 text-gold font-bold rounded-md" }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Upcoming windows</div>
                  {upcoming.length === 0 && <p className="text-xs text-gray-500">No dated deadlines yet. Add a date-style window (e.g. "Aug 31 2026") to a grant to see it here.</p>}
                  <div className="space-y-1.5">
                    {upcoming.map((g, i) => {
                      const days = Math.ceil((g._d - Date.now()) / 86400000);
                      return (
                        <div key={i} className="flex items-center justify-between gap-2 text-xs" data-testid={`cal-upcoming-${i}`}>
                          <span className="text-gray-300 truncate">{g.name}</span>
                          <span className={days <= 30 ? "text-amber-300 shrink-0" : "text-gray-500 shrink-0"}>
                            {g._d.toLocaleDateString()} · {days >= 0 ? `${days}d` : "passed"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="space-y-2">
            {pipeline.map((g, i) => (
              <div key={i} className="flex items-center justify-between gap-3 bg-navy-800 border border-white/10 p-3" data-testid={`pipeline-${i}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white truncate">{g.name}</span>
                    {g.status === "Awarded" && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-300 bg-green-500/15 border border-green-500/30 px-1.5 py-0.5 shrink-0" data-testid={`pipeline-funded-${i}`}>
                        <Award size={11} /> Funded!
                      </span>
                    )}
                    {closingSoon(g) && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 shrink-0" data-testid={`pipeline-closing-${i}`}>
                        <Clock size={11} /> Closing soon
                      </span>
                    )}
                  </div>
                  {g.deadline && <div className="text-[11px] text-gray-500">Window: {g.deadline}</div>}
                  {g.status === "Awarded" && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-[11px] text-gray-500">Amount secured $</span>
                      <input type="number" min="0" value={amtValue(i, g)} data-testid={`pipeline-amount-${i}`}
                        onChange={(e) => setAmtDraft((d) => ({ ...d, [i]: e.target.value }))}
                        onBlur={() => saveAmount(i)}
                        className="w-28 bg-navy border border-white/15 text-xs text-green-300 px-2 py-1 focus:border-gold focus:outline-none"
                        placeholder="0" />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={g.status || "Saved"} onChange={(e) => setStatus(i, e.target.value)} data-testid={`pipeline-status-${i}`}
                    className={`bg-navy border border-white/15 text-xs px-2 py-1 focus:border-gold focus:outline-none ${statusColor(g.status)}`}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => removeGrant(i)} data-testid={`pipeline-remove-${i}`} className="text-gray-500 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Suggested Grants</div>
      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-300 py-8 justify-center" data-testid="grants-loading">
          <Loader2 className="animate-spin" size={16} /> Finding relevant grants…
        </div>
      )}
      {!loading && err && <p className="text-sm text-red-400 py-6 text-center">Couldn't fetch grants right now. Please try again.</p>}
      {!loading && !err && grants.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">No grant suggestions available yet.</p>}
      {!loading && grants.length > 0 && (
        <div className="space-y-3" data-testid="grants-list">
          {grants.map((g, i) => (
            <label key={i} className={`block bg-navy-800 border p-4 cursor-pointer transition-colors ${selected[i] ? "border-gold/60" : "border-white/10 hover:border-white/25"}`} data-testid={`grant-${i}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={!!selected[i]} onChange={() => toggle(i)} data-testid={`grant-check-${i}`}
                  className="mt-1 accent-gold w-4 h-4" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">{g.name}</div>
                  {g.provider && <div className="text-xs text-gold mt-0.5">{g.provider}</div>}
                  {g.who_its_for && <p className="text-xs text-gray-400 mt-2"><span className="text-gray-500">Who it's for:</span> {g.who_its_for}</p>}
                  {g.why_fit && <p className="text-xs text-gray-300 mt-1"><span className="text-gray-500">Why it fits:</span> {g.why_fit}</p>}
                  {g.deadline && <p className="text-xs text-gray-300 mt-1"><span className="text-gray-500">Application window:</span> <span className="text-gold">{g.deadline}</span></p>}
                  {g.where_to_look && <p className="text-xs text-gray-400 mt-1"><span className="text-gray-500">Where to look:</span> {g.where_to_look}</p>}
                </div>
              </div>
            </label>
          ))}
          <p className="text-[11px] text-gray-500 italic pt-1">Always verify eligibility, deadlines and availability directly with each grant provider.</p>
          <div className="flex justify-end pt-2">
            <button onClick={saveGrants} disabled={saving || chosen.length === 0} data-testid="grants-save"
              className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2 text-sm font-semibold hover:bg-gold-light transition-colors disabled:opacity-50">
              {saving ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Add {chosen.length || ""} to pipeline
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}



const INPUT = "w-full bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none";

const Field = ({ label, testid, children }) => (
  <div data-testid={`bp-field-${testid}`}>
    <label className="text-xs text-gray-400 mb-1.5 block">{label}</label>
    {children}
  </div>
);

const Stat = ({ label, value, tone }) => (
  <div className="bg-navy-800 border border-white/10 p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className={`font-heading text-lg ${tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-red-400" : "text-white"}`}>{value}</div>
  </div>
);

function LineListEditor({ label, testid, lines, onAdd, onSet, onRm }) {
  return (
    <div data-testid={`bp-list-${testid}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-white font-medium">{label}</label>
        <button onClick={onAdd} data-testid={`bp-add-${testid}`} className="inline-flex items-center gap-1 text-xs text-gold hover:text-gold-light"><Plus size={13} /> Add line</button>
      </div>
      <div className="space-y-2">
        {(lines || []).length === 0 && <div className="text-xs text-gray-600">No lines yet — add one or use AI suggestions above.</div>}
        {(lines || []).map((ln, i) => (
          <div key={i} className="flex gap-2">
            <input value={ln.label || ""} onChange={(e) => onSet(i, "label", e.target.value)} placeholder="Item" data-testid={`bp-${testid}-label-${i}`} className={INPUT + " flex-1"} />
            <input type="number" value={ln.amount ?? ""} onChange={(e) => onSet(i, "amount", e.target.value)} placeholder="$" data-testid={`bp-${testid}-amount-${i}`} className={INPUT + " w-32"} />
            <button onClick={() => onRm(i)} data-testid={`bp-${testid}-rm-${i}`} className="text-gray-500 hover:text-red-400 px-2"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VersionsModal({ versionsUrl, onClose }) {
  const [data, setData] = useState(null);
  const [a, setA] = useState("current");
  const [b, setB] = useState(null);

  useEffect(() => {
    api.get(versionsUrl).then((r) => {
      setData(r.data);
      if (r.data.versions?.length) setB(String(r.data.versions[0].version));
    }).catch(() => toast.error("Could not load versions"));
  }, [versionsUrl]);

  const options = () => {
    const opts = [{ id: "current", label: data?.current?.label || "Current Draft" }];
    (data?.versions || []).forEach((v) => opts.push({ id: String(v.version), label: `Approved v${v.version}` }));
    return opts;
  };
  const snapFor = (id) => {
    if (id === "current") return data?.current;
    return (data?.versions || []).find((v) => String(v.version) === String(id));
  };

  const sA = snapFor(a);
  const sB = b ? snapFor(b) : null;
  const sectionsOf = (s) => (s?.sections || []);
  const compareRows = () => {
    if (!sA) return [];
    const bMap = {};
    sectionsOf(sB).forEach((x) => { bMap[x.key] = x; });
    return sectionsOf(sA).map((x) => {
      const other = bMap[x.key];
      let state = "same";
      if (sB) {
        if (!other) state = "new";
        else if ((other.content || "").trim() !== (x.content || "").trim()) state = "changed";
      }
      return { key: x.key, label: x.label, a: x.content, b: other?.content ?? "", state };
    });
  };

  const badge = { changed: "text-amber-300 bg-amber-500/15", new: "text-emerald-300 bg-emerald-500/15", same: "text-gray-500 bg-white/5" };

  return (
    <Modal title="Plan Versions" desc="Your plan is a living document. Review past approved versions and see what changed." onClose={onClose} testid="bp-versions-modal" wide>
      {!data ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gold" size={22} /></div> : (
        <>
          <div className="flex flex-wrap items-end gap-4" data-testid="bp-versions-picker">
            <div>
              <label className="text-xs text-gray-400 block mb-1">Version</label>
              <select value={a} onChange={(e) => setA(e.target.value)} data-testid="bp-version-a"
                className="bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none">
                {options().map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
            <div className="text-gray-500 pb-2">compared to</div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">Compare against</label>
              <select value={b || ""} onChange={(e) => setB(e.target.value || null)} data-testid="bp-version-b"
                className="bg-navy border border-white/15 text-white px-3 py-2 text-sm focus:border-gold focus:outline-none">
                <option value="">— None —</option>
                {options().filter((o) => o.id !== a).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {(data.versions || []).length === 0 && (
            <div className="text-sm text-gray-500 bg-white/5 border border-white/10 p-4" data-testid="bp-no-versions">
              No approved versions yet. When ATF approves your plan, each approval is saved here as a version you can revisit and compare.
            </div>
          )}

          <div className="space-y-3" data-testid="bp-version-compare">
            {compareRows().map((r) => (
              <div key={r.key} className="bg-navy-800 border border-white/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white text-sm font-medium">{r.label}</span>
                  {sB && <span className={`text-[10px] px-2 py-0.5 uppercase tracking-wider ${badge[r.state]}`}>{r.state === "same" ? "Unchanged" : r.state}</span>}
                </div>
                {sB ? (
                  <div className="grid sm:grid-cols-2 gap-3 text-xs">
                    <div className={`p-3 border ${r.state === "changed" ? "border-amber-500/30" : "border-white/5"} bg-navy`}>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{options().find((o) => o.id === a)?.label}</div>
                      <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">{r.a || <span className="text-gray-600 italic">Empty</span>}</div>
                    </div>
                    <div className={`p-3 border ${r.state === "changed" ? "border-amber-500/30" : "border-white/5"} bg-navy`}>
                      <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">{options().find((o) => o.id === b)?.label}</div>
                      <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">{r.b || <span className="text-gray-600 italic">Empty</span>}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-300 whitespace-pre-wrap leading-relaxed">{r.a || <span className="text-gray-600 italic">Empty</span>}</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

function Modal({ title, desc, onClose, children, testid, wide }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className={`bg-navy border border-white/10 w-full ${wide ? "max-w-3xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-navy z-10">
          <div>
            <div className="text-white font-heading text-lg">{title}</div>
            <div className="text-xs text-gray-500">{desc}</div>
          </div>
          <button onClick={onClose} data-testid={`${testid}-close`} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">{children}</div>
      </div>
    </div>
  );
}
